import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Calendar, Heart } from "lucide-react";
import { toast } from "sonner";
import { useUniversalTracking } from '@/hooks/useUniversalTracking';

interface AnniversaryPlanningExerciseProps {
  onBack: () => void;
}

export const AnniversaryPlanningExercise = ({ onBack }: AnniversaryPlanningExerciseProps) => {
  const { logExercise, isLogging } = useUniversalTracking();
  const [responses, setResponses] = useState({
    upcomingDate: '',
    significance: '',
    anticipatedDifficulties: '',
    supportNeeded: '',
    meaningfulRitual: '',
    selfCompassionPlan: '',
    backupPlan: ''
  });

  const handleSave = async () => {
    await logExercise({
      exercise_type: 'anniversary_planning',
      exercise_name: 'Anniversary Planning Journal',
      category: 'grief',
      duration_minutes: 15,
      completion_data: {
        has_date: !!responses.upcomingDate,
        sections_completed: Object.values(responses).filter(v => v.trim()).length
      },
      xp_earned: 20
    });
    toast.success('Your anniversary plan has been saved', {
      description: "You've prepared yourself with compassion"
    });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <Button variant="outline" onClick={onBack} className="mb-4">
        ← Back to Grief Tools
      </Button>

      <Card className="bg-gradient-therapeutic border-primary/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-soft-accent/10">
              <Calendar className="h-6 w-6 text-soft-accent" />
            </div>
            <div>
              <CardTitle className="text-2xl font-light">Anniversary Planning</CardTitle>
              <CardDescription>
                Prepare for difficult dates with intention and self-compassion
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Introduction */}
          <Card className="bg-gradient-calm border-border/50">
            <CardContent className="py-4 space-y-3">
              <p className="text-sm font-medium">Why Planning for Hard Dates Helps</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Birthdays, death anniversaries, holidays, or other significant dates can trigger intense grief waves. 
                Research shows that <span className="font-medium text-foreground">anticipating and preparing</span> for 
                these dates reduces anxiety and gives you a sense of agency. You're not avoiding the pain-you're meeting 
                it with intention rather than being ambushed by it.
              </p>
            </CardContent>
          </Card>

          {/* The Date */}
          <div className="space-y-2">
            <label className="text-sm font-medium">What date is approaching?</label>
            <Input
              type="text"
              value={responses.upcomingDate}
              onChange={(e) => setResponses({ ...responses, upcomingDate: e.target.value })}
              placeholder="Their birthday, anniversary of death, Christmas, our wedding anniversary..."
              className="bg-background/80"
            />
          </div>

          {/* Significance */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <span className="text-lg">💫</span>
              What does this date mean to you?
            </label>
            <Textarea
              value={responses.significance}
              onChange={(e) => setResponses({ ...responses, significance: e.target.value })}
              placeholder="This date represents... We used to... It's hard because..."
              className="min-h-[100px] bg-background/80 leading-relaxed"
            />
          </div>

          {/* Anticipated Difficulties */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <span className="text-lg">⚠️</span>
              What specific difficulties do you anticipate?
            </label>
            <p className="text-xs text-muted-foreground">
              Be honest. What will be hardest? Mornings? Evenings? Being alone? Being with others?
            </p>
            <Textarea
              value={responses.anticipatedDifficulties}
              onChange={(e) => setResponses({ ...responses, anticipatedDifficulties: e.target.value })}
              placeholder="Waking up that morning knowing... Seeing everyone else celebrate whilst I'm grieving... Social media posts..."
              className="min-h-[100px] bg-background/80 leading-relaxed"
            />
          </div>

          {/* Support Needed */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <span className="text-lg">🤝</span>
              What support do you need?
            </label>
            <p className="text-xs text-muted-foreground">
              Who can you reach out to? What do you need from others? (Company, space, understanding?)
            </p>
            <Textarea
              value={responses.supportNeeded}
              onChange={(e) => setResponses({ ...responses, supportNeeded: e.target.value })}
              placeholder="I'll tell [person] in advance that this day is hard... I need someone to check in... I want to be left alone but..."
              className="min-h-[100px] bg-background/80 leading-relaxed"
            />
          </div>

          {/* Meaningful Ritual */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <span className="text-lg">🕯️</span>
              What meaningful ritual or activity could honour this day?
            </label>
            <p className="text-xs text-muted-foreground">
              Something that acknowledges the loss whilst also caring for yourself
            </p>
            <Textarea
              value={responses.meaningfulRitual}
              onChange={(e) => setResponses({ ...responses, meaningfulRitual: e.target.value })}
              placeholder="Light a candle at their favourite spot, cook their favourite meal, donate to a cause, write them a letter, visit somewhere meaningful..."
              className="min-h-[100px] bg-background/80 leading-relaxed"
            />
          </div>

          {/* Self-Compassion Plan */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <span className="text-lg">💚</span>
              What acts of self-compassion will you commit to?
            </label>
            <p className="text-xs text-muted-foreground">
              How will you be gentle with yourself? What do you need to feel held?
            </p>
            <Textarea
              value={responses.selfCompassionPlan}
              onChange={(e) => setResponses({ ...responses, selfCompassionPlan: e.target.value })}
              placeholder="I'll take the day off work, I won't force myself to be 'fine', I'll let myself cry, I'll watch comforting films..."
              className="min-h-[100px] bg-background/80 leading-relaxed"
            />
          </div>

          {/* Backup Plan */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <span className="text-lg">🆘</span>
              Backup plan if it becomes overwhelming
            </label>
            <p className="text-xs text-muted-foreground">
              If the grief becomes too intense, what's your safety plan? Who can you call? What helps?
            </p>
            <Textarea
              value={responses.backupPlan}
              onChange={(e) => setResponses({ ...responses, backupPlan: e.target.value })}
              placeholder="I can call [name], I'll use my coping toolkit, I'll speak to THERAPPI, I know the crisis line is..."
              className="min-h-[100px] bg-background/80 leading-relaxed"
            />
          </div>

          {/* Mirror of Meaning */}
          <Card className="bg-gradient-calm border-border/50">
            <CardContent className="py-4 space-y-3">
              <p className="text-sm font-medium">Mirror of Meaning: You're Honouring Both the Loss and Your Needs</p>
              <div className="text-xs text-muted-foreground leading-relaxed space-y-2">
                <p>
                  By planning for this date, you're not "pre-grieving" or catastrophising. You're acknowledging reality 
                  with honesty and meeting yourself with compassion. That's incredibly mature grief work.
                </p>
                <p className="text-foreground">
                  You're allowed to both honour their memory AND take care of yourself. Those aren't in conflict. 
                  In fact, true honouring includes not destroying yourself in the process.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1">
              Save as Draft
            </Button>
            <Button className="flex-1" onClick={handleSave} disabled={isLogging}>
              <Heart className="w-4 h-4 mr-2" />
              {isLogging ? 'Saving...' : 'Save Anniversary Plan'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* The Science */}
      <Card className="bg-gradient-calm border-border/50">
        <CardContent className="py-4">
          <div className="space-y-3">
            <p className="text-sm font-medium">The Research on Anniversary Reactions</p>
            <div className="text-xs text-muted-foreground leading-relaxed space-y-2">
              <p>
                <span className="font-medium text-foreground">Anniversary reactions</span> are well-documented in grief 
                research. Your body and brain track time, even when you're not consciously thinking about it. As 
                significant dates approach, you may experience increased anxiety, sadness, or even physical symptoms.
              </p>
              <p>
                <span className="font-medium text-foreground">Why planning helps:</span> Anticipatory anxiety is often 
                worse than the event itself. When you make a plan, you shift from helpless anticipation to active coping. 
                This engages your prefrontal cortex (problem-solving) which helps regulate your amygdala (fear/emotion).
              </p>
              <p>
                <span className="font-medium text-foreground">Permission to change the plan:</span> On the day itself, 
                you may feel differently than expected. That's fine. This plan isn't rigid-it's a scaffold you can adapt. 
                The point is that you're not caught off guard.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
